/**
 * RequireRole — route guard for role-restricted pages.
 *
 * Usage in App.js:
 *     <Route path="users" element={
 *       <RequireRole roles={["super_admin","hq_admin","branch_admin"]}>
 *         <UserManagement />
 *       </RequireRole>
 *     } />
 *
 * Behaviour:
 *   * If no authenticated user → bounces to /login (handled upstream by
 *     AppLayout, but we double-check defensively).
 *   * If the user's role is in `roles` → renders children.
 *   * Otherwise → renders a friendly 403 panel with a "back to home" link.
 *
 * Why not just hide in the sidebar?
 *   Curious / paranoid users can paste URLs directly. Sidebar visibility is
 *   convenience; the route guard is the actual access control on the
 *   frontend. The backend ALWAYS enforces, but UI parity keeps logs clean
 *   and users out of confusing dead-ends.
 */
import { Link } from "react-router-dom";
import { ShieldWarning, House } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_HOME } from "@/lib/api";

export default function RequireRole({ roles, children, fallback }) {
  const { user } = useAuth();
  if (!user) return null;
  if (roles.includes(user.role)) return children;
  if (fallback) return fallback;

  const home = ROLE_HOME[user.role] || "/app/parent";
  return (
    <div className="max-w-md mx-auto py-16 text-center px-6" data-testid="role-guard-403">
      <ShieldWarning weight="duotone" size={64} className="mx-auto text-terracotta" />
      <h1 className="font-heading text-3xl font-bold mt-4">Not your turn yet.</h1>
      <p className="text-ink-soft mt-2">
        This page is reserved for administrators. If you think this is a mistake,
        ask your school admin to update your role.
      </p>
      <Link
        to={home}
        className="mt-6 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta-hover"
        data-testid="role-guard-home-btn"
      >
        <House weight="duotone" /> Back to home
      </Link>
    </div>
  );
}
